import { assign, emit, fromPromise, setup } from 'xstate';
import { EMPTY_ADDRESS_FORM } from '../../../constants';
import { Provider } from '../../../types';
import { showAxiosErrorAction } from '../../actions';
import {
  deleteAddress,
  deleteEmail,
  deletePhone,
  notifyVaultProviderUpdated,
  openAddressModal,
  resetModal,
  sendProfileSavedAlert,
  setAddEmailContext,
  setAddPhoneNumberContext,
  setCurrentTab,
  setEditAddressContext,
  setEditEmailAddressContext,
  setEditPhoneNumberContext,
  setLoadContext,
  setSubmitAddressContext,
  setSubmitEmailAddressContext,
  setSubmitPhoneNumbersContext,
  setUpdateContext,
} from './actions';
import { EMPTY_EMAIL_FORM, EMPTY_PHONE_FORM } from './constants';
import {
  Events,
  OnSetActiveTab,
  ProfileNameSaved,
  ProviderResponseEvent,
  UpdateEvent,
} from './events';
import { Context } from './types';
import {
  addressesProvider,
  emailAddressesProvider,
  personalInfoFromProvider,
  phoneNumbersProvider,
  professionalInfoFromProvider,
  sensitiveInfoFromProvider,
  updateProviderAndDetails,
} from './utils';

export const profileFormMachine = setup({
  actors: {
    updateProvider: fromPromise(updateProviderAndDetails),
  },
  actions: {
    sendError: showAxiosErrorAction,
    sendSuccess: sendProfileSavedAlert,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    notifyVaultProviderUpdated: notifyVaultProviderUpdated as any,
    // [MCV-1834] Runs before setUpdateContext, so personalInfo is still the name the form held.
    emitProfileNameSaved: emit(({ context, event }): ProfileNameSaved => {
      const saved = (event as ProviderResponseEvent).output?.data;
      return {
        type: 'PROFILE_NAME_SAVED',
        clerkUserId: saved?.clerkUserId ?? '',
        firstName: saved?.firstName ?? '',
        lastName: saved?.lastName ?? '',
        previousFirstName: context.personalInfo.firstName,
        previousLastName: context.personalInfo.lastName,
      };
    }),
    deleteEmail: assign(deleteEmail),
    deletePhone: assign(deletePhone),
    deleteAddress: assign(deleteAddress),
    setContextFromLoad: assign(setLoadContext),
    openAddressModal: assign(openAddressModal),
    resetModal: assign(resetModal),
    submitAddress: assign(setSubmitAddressContext),
    setEditAddressContext: assign(setEditAddressContext),
    setEditPhoneNumberContext: assign(setEditPhoneNumberContext),
    setAddPhoneNumberContext: assign(setAddPhoneNumberContext),
    setAddEmailContext: assign(setAddEmailContext),
    setSubmitPhoneNumbersContext: assign(setSubmitPhoneNumbersContext),
    setSubmitEmailAddressContext: assign(setSubmitEmailAddressContext),
    setUpdateContext: assign(setUpdateContext),
    setEditEmailAddressContext: assign(setEditEmailAddressContext),
    setActiveTab: assign(setCurrentTab),
    snapshotTab: assign({
      submittingTab: ({ context }) => context.currentTab,
    }),
  },

  types: {
    context: {} as Context,
    events: {} as Events,
    emitted: {} as ProfileNameSaved,
    input: {} as Partial<Context>,
  },
}).createMachine({
  context: ({ input }) => ({
    addresses: addressesProvider(input.provider),
    addressForm: EMPTY_ADDRESS_FORM,
    currentTab: input.currentTab as number,
    submittingTab: 0,
    editAddressIndex: -1,
    editPhoneNumberIndex: -1,
    editEmailAddressIndex: -1,
    emailAddresses: emailAddressesProvider(input.provider),
    emailAddressForm: EMPTY_EMAIL_FORM,
    errors: {},
    modal: {
      edit: false,
      create: false,
      type: 'address',
    },
    personalInfo: personalInfoFromProvider(input.provider),
    phoneNumberForm: EMPTY_PHONE_FORM,
    phoneNumbers: phoneNumbersProvider(input.provider),
    professionalInfo: professionalInfoFromProvider(input.provider),
    provider: input.provider as Provider,
    sensitiveInfo: sensitiveInfoFromProvider(input.provider),
  }),
  initial: '$_PERSONAL_INFO_FORM',
  on: {
    ON_LOAD: {
      actions: ['setContextFromLoad'],
    },
    ON_SET_ACTIVE_TAB: [
      {
        target: '.$_PERSONAL_INFO_FORM',
        actions: ['setActiveTab'],
        guard: ({ event }) => (event as OnSetActiveTab).tab === 0,
      },
      {
        target: '.$_SENSITIVE_INFO_FORM',
        actions: ['setActiveTab'],
        guard: ({ event }) => (event as OnSetActiveTab).tab === 1,
      },
      {
        target: '.$_PROFESSIONAL_INFO_FORM',
        actions: ['setActiveTab'],
        guard: ({ event }) => (event as OnSetActiveTab).tab === 2,
      },
      {
        target: '.$_CONTACT_INFO_VIEW',
        actions: ['setActiveTab'],
        guard: ({ event }) => (event as OnSetActiveTab).tab === 3,
      },
    ],
    ON_ERROR: {
      actions: ['sendError'],
    },
    ON_CANCEL_ADD_OR_EDIT_MODAL: {
      target: '.$_CONTACT_INFO_VIEW',
      actions: ['resetModal'],
    },
  },
  states: {
    $_ADDRESS_FORM: {
      on: {
        ON_SUBMIT_ADDRESSES: {
          target: '$_CONTACT_INFO_VIEW',
          actions: ['submitAddress'],
        },
      },
    },
    $_CONTACT_INFO_VIEW: {
      on: {
        ON_SUBMIT_CONTACT: {
          target: '$_SEND_UPDATE',
          actions: ['snapshotTab'],
        },
        ON_ADD_ADDRESS: {
          target: '$_ADDRESS_FORM',
          actions: ['openAddressModal'],
        },
        ON_EDIT_ADDRESS: {
          target: '$_ADDRESS_FORM',
          actions: ['setEditAddressContext'],
        },
        ON_ADD_PHONE_NUMBER: {
          target: '$_PHONE_NUMBER_FORM',
          actions: ['setAddPhoneNumberContext'],
        },
        ON_EDIT_PHONE_NUMBER: {
          target: '$_PHONE_NUMBER_FORM',
          actions: ['setEditPhoneNumberContext'],
        },
        ON_ADD_EMAIL_ADDRESS: {
          target: '$_EMAIL_ADDRESS_FORM',
          actions: [
            assign({ modal: { type: 'email', edit: false, create: false } }),
            'setAddEmailContext',
          ],
        },
        ON_EDIT_EMAIL_ADDRESS: {
          target: '$_EMAIL_ADDRESS_FORM',
          actions: ['setEditEmailAddressContext'],
        },
        ON_DELETE_EMAIL: {
          actions: ['deleteEmail'],
        },
        ON_DELETE_PHONE: {
          actions: ['deletePhone'],
        },
        ON_DELETE_ADDRESS: {
          actions: ['deleteAddress'],
        },
      },
    },
    $_EMAIL_ADDRESS_FORM: {
      on: {
        ON_SUBMIT_EMAIL_ADDRESSES: {
          target: '$_CONTACT_INFO_VIEW',
          actions: [
            assign({ modal: { type: 'email', edit: false, create: false } }),
            'setSubmitEmailAddressContext',
          ],
        },
      },
    },
    $_PERSONAL_INFO_FORM: {
      on: {
        ON_SUBMIT_PERSONAL: {
          target: '$_SEND_UPDATE',
          actions: 'snapshotTab',
        },
      },
    },
    $_PHONE_NUMBER_FORM: {
      on: {
        ON_SUBMIT_PHONE_NUMBERS: {
          target: '$_CONTACT_INFO_VIEW',
          actions: ['setSubmitPhoneNumbersContext'],
        },
      },
    },
    $_PROFESSIONAL_INFO_FORM: {
      on: {
        ON_SUBMIT_PROFESSIONAL: {
          target: '$_SEND_UPDATE',
          actions: ['snapshotTab'],
        },
      },
    },
    $_SENSITIVE_INFO_FORM: {
      on: {
        ON_SUBMIT_SENSITIVE: {
          target: '$_SEND_UPDATE',
          actions: ['snapshotTab'],
        },
      },
    },
    $_SEND_UPDATE: {
      invoke: {
        src: 'updateProvider',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: UpdateEvent },
        onDone: [
          {
            guard: ({ context }) => context.submittingTab === 0,
            target: '$_PERSONAL_INFO_FORM',
            actions: [
              'sendSuccess',
              'emitProfileNameSaved',
              'setUpdateContext',
              'notifyVaultProviderUpdated',
            ],
          },
          {
            guard: ({ context }) => context.submittingTab === 1,
            target: '$_SENSITIVE_INFO_FORM',
            actions: ['sendSuccess', 'setUpdateContext', 'notifyVaultProviderUpdated'],
          },
          {
            guard: ({ context }) => context.submittingTab === 2,
            target: '$_PROFESSIONAL_INFO_FORM',
            actions: ['sendSuccess', 'setUpdateContext', 'notifyVaultProviderUpdated'],
          },
          {
            target: '$_CONTACT_INFO_VIEW',
            actions: ['sendSuccess', 'setUpdateContext', 'notifyVaultProviderUpdated'],
          },
        ],
        onError: [
          {
            guard: ({ context }) => context.submittingTab === 0,
            target: '$_PERSONAL_INFO_FORM',
            actions: ['sendError'],
          },
          {
            guard: ({ context }) => context.submittingTab === 1,
            target: '$_SENSITIVE_INFO_FORM',
            actions: ['sendError'],
          },
          {
            guard: ({ context }) => context.submittingTab === 2,
            target: '$_PROFESSIONAL_INFO_FORM',
            actions: ['sendError'],
          },
          {
            target: '$_CONTACT_INFO_VIEW',
            actions: ['sendError'],
          },
        ],
      },
    },
  },
});
